import React, { useState } from 'react';
import {
    Box,
    Container,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import LiquorIcon from '@mui/icons-material/Liquor';
import CleanHandsIcon from '@mui/icons-material/CleanHands';
import SellIcon from '@mui/icons-material/Sell';
import HotelIcon from '@mui/icons-material/Hotel';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TagsSettings from './TagsSettings';
import ModuleSettings from './ModuleSettings';
import ReminderRulesSettings from './ReminderRulesSettings';
import DrinkOverview from '../drinks/DrinkOverview';
import CleaningFeesOverview from '../cleaningFees/CleaningFeesOverview';
import ReservationPriceOverview from '../ReservationPrice/ReservationPriceOverview';
import RoomOverview from '../rooms/RoomOverview';
import { SystemMailOverview } from '../mailing/systemmails/SystemMailOverview';
import ActivityLogPage from '../activity/ActivityLogPage';
import StatisticsPage from '../statistics/StatisticsPage';
import { GREEN_700, cardSx } from '../core/pvtaStyles';
import PageHeader from '../core/PageHeader';
import { isStaffOrSuperadmin } from '../utils/permission';

type SectionKey =
    | 'modules'
    | 'reminders'
    | 'tags'
    | 'drinks'
    | 'cleaning'
    | 'costs'
    | 'rooms'
    | 'systemmails'
    | 'activity'
    | 'statistics';

interface SettingsSection {
    key: SectionKey;
    label: string;
    icon: React.ReactNode;
}

const SECTIONS: SettingsSection[] = [
    { key: 'modules', label: 'Funktionen', icon: <TuneIcon /> },
    { key: 'reminders', label: 'Erinnerungen', icon: <NotificationsActiveIcon /> },
    { key: 'tags', label: 'Tags / Schlagworte', icon: <LocalOfferOutlinedIcon /> },
    { key: 'drinks', label: 'Getränke', icon: <LiquorIcon /> },
    { key: 'cleaning', label: 'Reinigung', icon: <CleanHandsIcon /> },
    { key: 'costs', label: 'Kosten', icon: <SellIcon /> },
    { key: 'rooms', label: 'Zimmer', icon: <HotelIcon /> },
    { key: 'systemmails', label: 'Systemnachrichten', icon: <MarkEmailUnreadIcon /> },
    { key: 'activity', label: 'Aktivitätenlog', icon: <HistoryEduIcon /> },
    { key: 'statistics', label: 'Statistiken', icon: <BarChartIcon /> },
];

const SettingsPage: React.FC = () => {
    // Funktionen (Modulschalter) nur für is_staff / is_superuser — gruppenunabhängig.
    const canManageModules = isStaffOrSuperadmin();
    const visibleSections = canManageModules
        ? SECTIONS
        : SECTIONS.filter((section) => section.key !== 'modules' && section.key !== 'reminders');

    const [activeSection, setActiveSection] = useState<SectionKey>(
        canManageModules ? 'modules' : 'tags',
    );

    const renderSection = (): React.ReactNode => {
        switch (activeSection) {
            case 'modules':
                if (!canManageModules) {
                    return null;
                }
                return (
                    <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3 }, minWidth: 0 }}>
                        <ModuleSettings />
                    </Box>
                );
            case 'reminders':
                if (!canManageModules) {
                    return null;
                }
                return <ReminderRulesSettings />;
            case 'tags':
                return (
                    <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3 }, minWidth: 0 }}>
                        <TagsSettings />
                    </Box>
                );
            case 'drinks':
                return <DrinkOverview />;
            case 'cleaning':
                return <CleaningFeesOverview />;
            case 'costs':
                return <ReservationPriceOverview />;
            case 'rooms':
                return <RoomOverview />;
            case 'systemmails':
                return <SystemMailOverview />;
            case 'activity':
                return <ActivityLogPage />;
            case 'statistics':
                return <StatisticsPage />;
            default:
                return null;
        }
    };

    return (
        <Box sx={{ py: { xs: 4, md: 5 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
                <PageHeader
                    title="Einstellungen"
                    subtitle="Verwalte zentrale Einstellungen und Stammdaten der Verbindung."
                />

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' },
                        gap: 3,
                        alignItems: 'start',
                    }}
                >
                    <Box sx={{ ...cardSx, p: 1 }}>
                        <List disablePadding>
                            {visibleSections.map((section) => {
                                const active = activeSection === section.key;
                                return (
                                    <ListItemButton
                                        key={section.key}
                                        selected={active}
                                        onClick={() => setActiveSection(section.key)}
                                        sx={{
                                            borderRadius: 'var(--pvta-radius-sm)',
                                            '&.Mui-selected': {
                                                backgroundColor: 'var(--pvta-green-100)',
                                                color: GREEN_700,
                                                '& .MuiListItemIcon-root': { color: GREEN_700 },
                                                '&:hover': { backgroundColor: 'var(--pvta-green-100)' },
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 38 }}>{section.icon}</ListItemIcon>
                                        <ListItemText primary={section.label} primaryTypographyProps={{ fontWeight: 600 }} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>{renderSection()}</Box>
                </Box>
            </Container>
        </Box>
    );
};

export default SettingsPage;
